// src/lib/kinema/konva/hooks.ts
import { useState, useEffect, useMemo } from 'react';
import { useEditorContext } from './EditorProvider';
import type { Editor } from '../core/Editor';
import type { BaseLayerData, EditorState } from '../core/types';
import { getCompositionProps } from '../core/types';
import { getRootLayers, getChildren } from '../core/layers/treeUtils';
import { resolveLayerAtTime } from '../core/effects/resolveLayer';

export function useEditor(): Editor {
  return useEditorContext();
}

export function useEditorState(): EditorState {
  const editor = useEditorContext();
  const [state, setState] = useState<EditorState>(editor.getState());

  useEffect(() => {
    return editor.subscribe((newState) => setState(newState));
  }, [editor]);

  return state;
}

export function useLayerTree() {
  const state = useEditorState();

  return useMemo(() => ({
    roots: getRootLayers(state.layers, state.rootLayerId),
    getChildren: (parentId: string) => getChildren(state.layers, parentId),
  }), [state.layers, state.rootLayerId]);
}

export function useLayer(layerId: string | null): BaseLayerData | null {
  const state = useEditorState();
  if (!layerId) return null;
  return state.layers[layerId] ?? null;
}

export function useResolvedLayer(layerId: string | null): BaseLayerData | null {
  const state = useEditorState();
  const editor = useEditorContext();
  if (!layerId) return null;
  const layer = state.layers[layerId];
  if (!layer) return null;

  return resolveLayerAtTime(
    layer,
    state.playheadTime,
    editor.getInterpolatorRegistry().getAll(),
  );
}

export function usePlayhead() {
  const state = useEditorState();
  const comp = getCompositionProps(state);
  return {
    time: state.playheadTime,
    isPlaying: state.isPlaying,
    duration: comp.duration,
    frameRate: comp.frameRate,
  };
}

export function useComposition() {
  const state = useEditorState();
  return getCompositionProps(state);
}

export function useSelection() {
  const state = useEditorState();
  return {
    selectedIds: state.selectedIds,
    primary: state.primarySelectedId,
  };
}
